import { QueryClient, UndefinedInitialDataOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEqual } from "lodash-es";
import { useEffect, useMemo } from "react";

import { handleAppError } from "@/utils";

import { useDepartmentRoute } from "./use-department-route";

export interface UseFetchOptions<TRaw, TParsed = TRaw>
  extends Omit<UndefinedInitialDataOptions<TRaw, unknown, TParsed>, "queryFn"> {
  toastOnError?: boolean;
  treatNullAsError?: boolean;
  includeDepartmentInCache?: boolean;
}

const DEFAULT_QUERY_KEY = ["use-fetch"];

const defaultOptions = {
  queryKey: DEFAULT_QUERY_KEY,
  toastOnError: true,
  includeDepartmentInCache: false,
} as const;

export function useFetch<TRaw, TParsed = TRaw>(
  callback: () => Promise<TRaw>,
  options: UseFetchOptions<TRaw, TParsed> = defaultOptions as UseFetchOptions<TRaw, TParsed>,
) {
  options = { ...defaultOptions, ...options };

  const { department } = useDepartmentRoute();
  const defaultClient = useQueryClient();

  // Se includeDepartmentInCache for true, adicionar department no início da queryKey
  const queryKey =
    options.includeDepartmentInCache && department ? [...(options.queryKey || []), department] : options.queryKey;

  const result = useQuery(
    {
      ...options,
      queryKey,
      retry: false,
      queryFn: callback,
    },
    isEqual(queryKey, DEFAULT_QUERY_KEY)
      ? new QueryClient({
          defaultOptions: {
            queries: {
              staleTime: 0,
            },
          },
        })
      : defaultClient,
  );

  const isNull = useMemo(() => {
    return !!options.treatNullAsError && result.data === null;
  }, [options.treatNullAsError, result.data]);

  useEffect(() => {
    if (options.toastOnError && (result.error || isNull)) {
      handleAppError(result.error, { handleUnknownError: true });
    }
  }, [result.error, options.toastOnError, isNull]);

  return {
    ...result,
    isPendingOrErrored: result.isError || result.isLoading || isNull,
  };
}
